// lightGallery removes its <video> elements without releasing them. closeGallery
// runs $inner.empty(), and the video plugin defines no closeGallery hook, so a
// gallery close orphans every player it built and each one keeps an active media
// resource. Moving between slides only pauses the one being left, which holds its
// resource too, so walking a set of videos ends holding one player per slide.
//
// Both CardPhotos.vue and TabObservation.vue instantiate lightGallery with the
// video plugin, so both need this.

const STASHED_SOURCES = '__releasedVideoSources'

// .lg-video-object is also the class on youtube, vimeo and wistia iframes.
const videosIn = (root) =>
  Array.from(root.querySelectorAll('.lg-video-object')).filter(
    (el) => el.tagName === 'VIDEO'
  )

/**
 * Frees the media resource each <video> under `root` holds.
 *
 * The gallery markup carries its sources as child <source> nodes rather than a
 * src attribute, so removing those children and calling load() is what moves
 * networkState to EMPTY. The removed nodes are stashed on the element for
 * restoreVideos, because loadContent guards the video markup behind a
 * lg-loaded class: it is built once per slide per open and nothing re-appends
 * it when the reader navigates back.
 *
 * @param {HTMLElement|null} root - a slide element, or the gallery's outer
 *   container to cover every slide at once
 */
export const releaseVideos = (root) => {
  if (!root) return

  videosIn(root).forEach((el) => {
    const sources = Array.from(el.querySelectorAll('source'))
    if (sources.length) el[STASHED_SOURCES] = sources

    el.pause()
    el.removeAttribute('src')
    sources.forEach((source) => el.removeChild(source))
    el.load()
  })
}

/**
 * Puts back the sources releaseVideos took off, leaving the element in the state
 * it would have been in had it never been released.
 *
 * @param {HTMLElement|null} root - the slide element being navigated to
 */
export const restoreVideos = (root) => {
  if (!root) return

  videosIn(root).forEach((el) => {
    const sources = el[STASHED_SOURCES]
    if (!sources || el.querySelector('source')) return

    sources.forEach((source) => el.appendChild(source))
    el.load()
  })
}

/**
 * The DOM element for one slide of a lightGallery instance, or null.
 *
 * @param {Object} gallery - the instance window.lightGallery() returned
 * @param {number} index
 */
export const slideElement = (gallery, index) => {
  if (!gallery || typeof index !== 'number') return null

  const slide = gallery.getSlideItem(index)
  return (slide && slide.get()) || null
}

/**
 * Builds the two listeners that keep a gallery's videos from accumulating, for
 * a component to add to its gallery element and remove on destroy.
 *
 * lgAfterClose is too late for the close listener: closeGallery has already run
 * $inner.empty() by then and the elements can no longer be found.
 *
 * @param {Function} getGallery - returns the lightGallery instance, which does
 *   not exist yet when a component registers these
 * @returns {{ onBeforeSlide: Function, onBeforeClose: Function }}
 */
export const buildVideoReleaseListeners = (getGallery) => ({
  onBeforeSlide: (event) => {
    const gallery = getGallery()
    // lGalleryOn is false until the gallery has opened. The video plugin guards
    // its own beforeSlide handler the same way, because prevIndex falls back to
    // 0 on open and would otherwise point at the slide being opened.
    if (!gallery || !gallery.lGalleryOn) return

    const { prevIndex, index } = event.detail
    if (prevIndex === index) return

    releaseVideos(slideElement(gallery, prevIndex))
    restoreVideos(slideElement(gallery, index))
  },
  onBeforeClose: () => {
    const gallery = getGallery()
    if (!gallery || !gallery.outer) return

    releaseVideos(gallery.outer.get())
  },
})
